'use client';

import { useEffect, useState, type MutableRefObject } from 'react';
import type { Session } from '@supabase/supabase-js';
import { apiClient } from '@/lib/api-client';
import { createClient } from '@/lib/supabase/client';
import { getBrowserSession, isSessionUsable } from '@/lib/supabase/session-client';
import { Logger } from '@/lib/logger';
import { isActiveStatus, POLL_INTERVAL_ACTIVE, POLL_INTERVAL_IDLE, STALE_THRESHOLD_MS } from './use-scan-status.types';
import type { ScanJobData, ScanStatusState } from './use-scan-status.types';

type LifecycleOptions = {
  authenticated: boolean;
  activeStatus: ScanJobData['status'] | undefined;
  realtimeLive: boolean;
  mountedRef: MutableRefObject<boolean>;
  fetchCurrentJob: () => Promise<ScanJobData | null | undefined>;
  fetchStatus: () => Promise<ScanJobData | null | undefined>;
  subscribeToJob: (id: string) => void;
  cleanupSubscription: () => void;
  setState: (updater: (previous: ScanStatusState) => ScanStatusState) => void;
};

export function useScanAuthentication(
  cleanupSubscription: LifecycleOptions['cleanupSubscription'],
  setState: LifecycleOptions['setState'],
) {
  const [authenticated, setAuthenticated] = useState(false);
  useEffect(() => {
    let canceled = false;
    const supabase = createClient();
    const applyAuthState = (session: Session | null) => {
      if (canceled) return;
      const usable = isSessionUsable(session);
      setAuthenticated(usable);
      if (!usable) {
        cleanupSubscription();
        setState(() => ({
          currentJob: null, lastCompletedJob: null, gmailStatus: null,
          isLoading: false, error: null, progress: 0, tier: 'free', emailsLimit: null,
        }));
      }
    };
    void getBrowserSession(supabase).then(applyAuthState).catch((error) => {
      Logger.warn('Unable to verify scan session', { error });
      applyAuthState(null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => applyAuthState(session));
    return () => {
      canceled = true;
      subscription.unsubscribe();
    };
  }, [cleanupSubscription, setState]);
  return authenticated;
}

export function useScanStatusLifecycle({
  authenticated, activeStatus, realtimeLive, mountedRef, fetchCurrentJob,
  fetchStatus, subscribeToJob, cleanupSubscription, setState,
}: LifecycleOptions) {
  useEffect(() => {
    const active = isActiveStatus(activeStatus);
    if (!authenticated || (active && realtimeLive)) return;
    const timer = setInterval(() => {
      void (active ? fetchCurrentJob() : fetchStatus());
    }, active ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_IDLE);
    return () => clearInterval(timer);
  }, [authenticated, activeStatus, realtimeLive, fetchCurrentJob, fetchStatus]);

  useEffect(() => {
    if (!authenticated) {
      cleanupSubscription();
      return;
    }
    mountedRef.current = true;
    const initialize = async () => {
      setState((previous) => ({ ...previous, isLoading: true, error: null }));
      const job = await fetchStatus();
      if (!mountedRef.current || job === undefined) return;
      if (job?.id && isActiveStatus(job.status)) {
        subscribeToJob(job.id);
        return;
      }
      try {
        const gmail = await apiClient.auth.getGmailOauthStatus();
        if (!gmail?.isConnected || gmail.hasRequiredScopes === false) return;
        const history = await apiClient.scan.history.query({ limit: 1 });
        const latest = history?.jobs?.[0];
        if (latest?.status !== 'COMPLETED' || latest.completion_quality === 'partial' || !latest.completed_at) return;
        const elapsed = Date.now() - new Date(latest.completed_at).getTime();
        if (!Number.isFinite(elapsed) || elapsed <= STALE_THRESHOLD_MS || !mountedRef.current) return;
        const result = await apiClient.scan.start.mutate({ intent: 'automatic' });
        if (!result?.job_id) return;
        const newJob = await apiClient.scan.current.query();
        if (newJob?.id && mountedRef.current) {
          setState((previous) => ({ ...previous, currentJob: newJob, progress: 0 }));
          subscribeToJob(newJob.id);
        }
      } catch (error) {
        Logger.warn('Automatic stale scan refresh did not start', { error });
      }
    };
    void initialize();
    return () => {
      mountedRef.current = false;
      cleanupSubscription();
    };
  }, [authenticated, cleanupSubscription, fetchStatus, mountedRef, setState, subscribeToJob]);
}
